import { createApp, nextTick, watch } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import router from './router'
import { waitForPageStyles } from './startup-styles'
import { useUserStore } from './stores/user'
import { SESSION_EXPIRED_EVENT } from './auth/session'
import { accessRedirect } from './auth/navigation'
import { loginLocation } from './auth/return-to'
import '@fontsource/bricolage-grotesque/latin-600.css'
import '@fontsource/bricolage-grotesque/latin-700.css'
import '@fontsource/spline-sans-mono/latin-400.css'
import '@fontsource/spline-sans-mono/latin-500.css'
import './styles/app.css'

declare global {
  interface Window {
    __cquBoot?: { ready(): void; fail(reason?: string): void; wait(): void; step(stage: 'entry' | 'router' | 'styles' | 'mounted'): void }
  }
}

window.__cquBoot?.step('entry')
const app = createApp(App)
app.use(createPinia())
app.use(router)
const userStore = useUserStore()
const expired = () => { void router.replace(loginLocation(router.currentRoute.value)) }
window.addEventListener(SESSION_EXPIRED_EVENT, expired)
const unwatchRole = watch(() => userStore.userRole, () => {
  const name = accessRedirect(router.currentRoute.value, {
    loggedIn: userStore.isLoggedIn, role: userStore.userRole,
  })
  if (name) void router.replace(name === 'Login' ? loginLocation(router.currentRoute.value) : { name })
})
if (import.meta.hot) import.meta.hot.dispose(() => {
  window.removeEventListener(SESSION_EXPIRED_EVENT, expired)
  unwatchRole()
})
// Keep a real, dependency-free recovery screen until the first route is ready.
router.onError(() => window.__cquBoot?.fail('router'))
let renderFailed = false
let mounted = false
let stylesReady = false
function reveal() {
  if (mounted && stylesReady && !renderFailed) window.__cquBoot?.ready()
}
app.config.errorHandler = (error) => {
  renderFailed = true
  console.error(error)
  window.__cquBoot?.fail('render')
}
router.beforeEach(() => window.__cquBoot?.wait())
router.afterEach((_to, _from, failure) => {
  if (!failure)
    void nextTick().then(() => {
      reveal()
    })
})
// The route mounts independently of CSS: login, APIs and scene downloads must
// not be held hostage by a lost stylesheet event. Keep the small boot overlay
// until CSS is actually attached, so the full page is never shown unstyled.
void waitForPageStyles().then(() => {
  stylesReady = true
  window.__cquBoot?.step('styles')
  reveal()
}).catch(() => window.__cquBoot?.fail('styles'))

void router.isReady().then(() => {
  window.__cquBoot?.step('router')
  app.mount('#app')
  mounted = true
  window.__cquBoot?.step('mounted')
  reveal()
  // Reconcile cached identity without putting a network request on the startup gate.
  void userStore.refreshSession().catch(() => {})
}).catch(() => window.__cquBoot?.fail('mount'))
